import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  InconsistencyLevel,
  InconsistencySource,
  InconsistencyType,
} from '../../../../../../../../backend/src/case/inconsistency/inconsistency-display';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { Label } from '@/components/atoms/label';
import MultiSelect from '@/components/atoms/MultiSelect';
import TooltipIcon from '@/components/atoms/TooltipIcon';
import { DocumentFilter } from '@/components/molecules/DocumentFilter';
import FilterPopup from '@/components/molecules/FilterPopup';
import { FilterSwitch } from '@/components/molecules/FilterSwitch';
import { transformFilterName } from '@/helpers/filterNames';
import type { InconsistencyFilters as InconsistencyFiltersType, MyOptionType } from '@/types';

export const InconsistencyFiltersPopup = ({
  activeFilters,
  setFilters,
  handleClose,
  goToPage,
}: {
  activeFilters: InconsistencyFiltersType;
  setFilters: (v: InconsistencyFiltersType) => void;
  handleClose: () => void;
  goToPage?: (value: number) => void;
}) => {
  const { t } = useTranslation();

  const currentIncludedDocs = activeFilters['docIds'] || [];
  const currentExcludedDocs = activeFilters['excludedDocIds'] || [];

  // Use maps for included/excluded docs for better performance
  const [includedDocs, setIncludedDocs] = useState<Map<string, MyOptionType>>(
    new Map(currentIncludedDocs.filter((doc) => doc !== null).map((doc) => [doc.value, doc])),
  );
  const [excludedDocs, setExcludedDocs] = useState<Map<string, MyOptionType>>(
    new Map(currentExcludedDocs.filter((doc) => doc !== null).map((doc) => [doc.value, doc])),
  );

  const [levelFilters, setLevelFilters] = useState<MyOptionType[]>(activeFilters['level'] || []);
  const [typeFilters, setTypeFilters] = useState<MyOptionType[]>(activeFilters['type'] || []);
  const [sourceFilters, setSourceFilters] = useState<MyOptionType[]>(activeFilters['source'] || []);
  const [hasRelevantFactsFilter, setHasRelevantFactsFilter] = useState<boolean | undefined>(
    activeFilters.hasRelevantFacts,
  );

  const handleSubmit = () => {
    const newFilters = { ...activeFilters };
    newFilters['docIds'] = Array.from(includedDocs.values());
    newFilters['excludedDocIds'] = Array.from(excludedDocs.values());
    newFilters['level'] = levelFilters;
    newFilters['type'] = typeFilters;
    newFilters['source'] = sourceFilters;
    newFilters['hasRelevantFacts'] = hasRelevantFactsFilter;

    setFilters(newFilters);
    handleClose();
  };
  const handleClear = () => {
    setFilters({ docIds: [], excludedDocIds: [], level: [], type: [], source: [] });
    setHasRelevantFactsFilter(undefined);
    handleClose();
  };

  return (
    <FilterPopup
      title="Filter Inconsistencies"
      description="Narrow down your inconsistencies search"
      onApply={handleSubmit}
      onClear={handleClear}
      goToPage={goToPage}
    >
      {/* Documents Filter */}
      <div>
        <Label className="mb-2 block">Documents</Label>
        <DocumentFilter
          includedDocs={includedDocs}
          excludedDocs={excludedDocs}
          setIncludedDocs={setIncludedDocs}
          setExcludedDocs={setExcludedDocs}
          mode="include-facts-from"
        />
      </div>
      {/* Relevant Facts Filter */}
      <FilterSwitch
        id="relevantfacts-switch"
        label="Contains Relevant Facts"
        checked={hasRelevantFactsFilter ?? false}
        onCheckedChange={(checked) => {
          setHasRelevantFactsFilter(checked ? true : undefined);
        }}
      />

      {/* Severity Filter */}
      <div>
        <Label className="mb-2 block">Severity</Label>
        <MultiSelect
          placeholder="All severity levels"
          options={Object.values(InconsistencyLevel).map((level) => ({
            label: transformFilterName(level),
            value: level,
          }))}
          selectedOptions={levelFilters}
          onChange={(newValue) => {
            trackCustomEvent(TrackingEvents.FilterInconsistenciesBySeverity);
            setLevelFilters([...newValue]);
          }}
          className="w-full"
        />
      </div>
      {/* Source Filter */}
      <div>
        <div className="mb-2 flex items-center gap-2">
          <Label className="block">Source</Label>
          <TooltipIcon
            tooltipContent={`Where the inconsistency was found, e.g. within a single claim (i.e. ${t('witness statement')}), across claims, between claims and other evidence, or between different pieces of evidence.`}
          />
        </div>
        <MultiSelect
          placeholder="All sources"
          options={Object.values(InconsistencySource).map((source) => ({
            label: transformFilterName(source, 'inconsistencySource'),
            value: source,
          }))}
          selectedOptions={sourceFilters}
          onChange={(newValue) => {
            trackCustomEvent(TrackingEvents.FilterInconsistenciesBySource);
            setSourceFilters([...newValue]);
          }}
          className="w-full"
        />
      </div>
      {/* Type Filter */}
      <div>
        <div className="mb-2 flex items-center gap-2">
          <Label className="block">Type</Label>
          <TooltipIcon tooltipContent="The nature of the inconsistency: dates, events, changing testimony, details, observations, or potential fabrications." />
        </div>
        <MultiSelect
          placeholder="All types"
          options={Object.values(InconsistencyType).map((type) => ({
            label: transformFilterName(type, 'inconsistencyType'),
            value: type,
          }))}
          selectedOptions={typeFilters}
          onChange={(newValue) => {
            trackCustomEvent(TrackingEvents.FilterInconsistenciesByType);
            setTypeFilters([...newValue]);
          }}
          className="w-full"
        />
      </div>
    </FilterPopup>
  );
};
